import { Component, inject, OnInit, OnDestroy, TemplateRef } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter, Subscription } from 'rxjs';
import { NgbOffcanvas, NgbOffcanvasModule } from '@ng-bootstrap/ng-bootstrap';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faXmark, faCaretRight, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { QuizStateService } from '../../services/quiz-state.service';
import { QuizNavigationService } from '../../services/quiz-navigation.service';
import { QUIZ_NAVIGATION_ITEMS } from '../../config/quiz-navigation-config';
import { NavigationActionService } from '../../services/navigation-action.service';
import { LoadingButtonComponent } from '../../shared-components/loading-button/loading-button.component';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-quiz-wrapper',
    imports: [
        RouterOutlet,
        RouterLink,
        RouterLinkActive,
        CommonModule,
        NgbOffcanvasModule,
        FontAwesomeModule,
        LoadingButtonComponent
    ],
    styleUrls: ['./quiz-wrapper.component.scss'],
    template: `
    <div class="container position-relative">
      <!-- Toggle button for small screens -->
      <button
        class="btn btn-light menu-button d-md-none"
        aria-label="Open navigation menu"
        (click)="openNav(content)">
        <span class="custom-icon">
          <span class="line"></span>
          <span class="line"></span>
          <fa-icon [icon]="caretIcon" class="caret"></fa-icon>
        </span>
      </button>

      @if (quizState.quizNameValue()) {
        <div class="row mt-3 mb-3">
          <div class="col-12">
            <h4 class="quiz-title">{{ quizState.quizNameValue() }}</h4>
          </div>
        </div>
      }

      <div class="row mb-4">
        <!-- Sidebar for medium+ screens -->
        <div class="col-md-3 d-none d-md-block">
          <ng-container [ngTemplateOutlet]="navigationList"></ng-container>
        </div>

        <!-- Main content area -->
        <div class="col-12 col-md-9 content-area">
          <router-outlet></router-outlet>

          <!-- Navigation buttons -->
          <div class="quiz-navigation-buttons d-flex justify-content-between mt-4">
            <div class="flex-grow-0 nav-button-space">
              @if (navService.hasPrevious()) {
                <button
                  class="btn border-0 quiz-navigation-button--prev"
                  [routerLink]="navService.previousRoute() || []">
                  <fa-icon [icon]="arrowLeftIcon" class="me-2"></fa-icon>
                  Previous
                </button>
              }
            </div>

            <!-- Middle action button from navigation service -->
            <div class="d-flex justify-content-center flex-grow-1">
              @if (navigationActionService.middleAction()) {
                <app-loading-button
                  [type]="'button'"
                  [disabled]="navigationActionService.middleAction()?.disabled || false"
                  [loading]="navigationActionService.middleAction()?.loading || false"
                  [testId]="navigationActionService.middleAction()?.testId || ''"
                  (buttonClick)="executeMiddleAction()">
                  {{navigationActionService.middleAction()?.label}}
                </app-loading-button>
              }
            </div>

            <div class="flex-grow-0 nav-button-space">
              @if (navService.hasNext()) {
                <button
                  class="btn border-0 quiz-navigation-button--next"
                  [routerLink]="navService.nextRoute() || []">
                  Next
                  <fa-icon [icon]="arrowRightIcon" class="ms-2"></fa-icon>
                </button>
              }
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation list template -->
    <ng-template #navigationList>
      <ul class="nav flex-column">
        @for (item of navigationItems; track item.path) {
          <li class="nav-item">
              <a
                class="nav-link text-black"
                [routerLink]="getRouterLink(item)"
                routerLinkActive="active"
                [routerLinkActiveOptions]="{exact: item.path === ''}"
                [class.disabled]="item.requiresExistingQuiz && isNewQuiz"
                [attr.tabindex]="item.requiresExistingQuiz && isNewQuiz ? '-1' : null"
                [attr.aria-disabled]="item.requiresExistingQuiz && isNewQuiz ? 'true' : null">
                {{ item.label }}
              </a>
          </li>
        }
        <li class="nav-item">
          <a
            class="nav-link text-black d-flex align-items-center"
            [href]="adminGuideUrl"
            target="_blank"
            rel="noopener noreferrer">
            Reference Guide
          </a>
        </li>
      </ul>
    </ng-template>

    <!-- Offcanvas template -->
    <ng-template #content let-offcanvas>
      <div class="offcanvas-header">
        <h4 class="offcanvas-title">Quiz Navigation</h4>
        <button type="button" class="btn-close" aria-label="Close" (click)="offcanvas.dismiss()">
        </button>
      </div>
      <div class="offcanvas-body">
        <ng-container [ngTemplateOutlet]="navigationList"></ng-container>
      </div>
    </ng-template>
  `
})
export class QuizWrapperComponent implements OnInit, OnDestroy {
  quizGuid: string = '';
  isNewQuiz: boolean = false;
  private routerSubscription: Subscription | null = null;

  // Use the shared navigation items
  readonly navigationItems = QUIZ_NAVIGATION_ITEMS;

  // FontAwesome icons
  closeIcon = faXmark;
  caretIcon = faCaretRight;
  arrowLeftIcon = faArrowLeft;
  arrowRightIcon = faArrowRight;

  get adminGuideUrl(): string {
    return `${environment.catalogUrl}/info/admin_guide`;
  }

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private offcanvasService = inject(NgbOffcanvas);
  quizState = inject(QuizStateService);
  navService = inject(QuizNavigationService);
  navigationActionService = inject(NavigationActionService);

  ngOnInit(): void {
    // Get the current quizGuid from the route params
    this.route.firstChild?.paramMap.subscribe(params => {
      const guid = params.get('quizGuid');
      if (guid) {
        this.quizGuid = guid;
        this.isNewQuiz = guid === 'new';
        // Update the navigation service with the current quiz guid
        this.navService.updateQuizGuid(this.quizGuid, this.isNewQuiz);
      }
    });

    // Subscribe to router events to update when navigation occurs
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.checkCurrentRoute(event.url);
      // Close offcanvas when navigation occurs
      this.offcanvasService.dismiss();
    });

    // Initial check on component init
    this.checkCurrentRoute(this.router.url);
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  // Open the offcanvas navigation
  openNav(content: TemplateRef<any>): void {
    this.offcanvasService.open(content, { position: 'start' });
  }

  private checkCurrentRoute(url: string): void {
    const urlParts = url.split('/');

    // Check if we're in a "new" quiz route
    if (urlParts.includes('new')) {
      this.isNewQuiz = true;
      this.quizGuid = 'new';
    } else {
      // Find the index of "quizzes" in the URL
      const quizzesIndex = urlParts.findIndex(part => part === 'quizzes');
      if (quizzesIndex !== -1 && quizzesIndex + 1 < urlParts.length) {
        // The quiz GUID should be the part after "quizzes"
        const guid = urlParts[quizzesIndex + 1];
        if (guid && guid !== this.quizGuid) {
          this.quizGuid = guid;
          this.isNewQuiz = guid === 'new';
          // Update the navigation service with the new quiz guid
          this.navService.updateQuizGuid(this.quizGuid, this.isNewQuiz);
        }
      }
    }
  }

  /**
   * Returns the correct router link array for a navigation item,
   * handling empty paths correctly to avoid trailing slashes
   */
  getRouterLink(item: { path: string }): string[] {
    // For the quiz details page (empty path), we need to handle it separately
    // to avoid the trailing slash issue
    if (item.path === '') {
      return ['/quizzes', this.quizGuid];
    }

    return ['/quizzes', this.quizGuid, item.path];
  }

  /**
   * Executes the middle action from the navigation service
   */
  executeMiddleAction(): void {
    const middleAction = this.navigationActionService.middleAction();
    if (middleAction && middleAction.action) {
      middleAction.action();
    }
  }
}
